<template>
    <div>
        <v-card elevation="1">
            <v-card-text>
                <div class="caption mb-4">
                    <p>
                        This step saves a signed form which releases Elite Logistics from any insurance or Workers' Comp liability
                        for you or your employees, if any.
                    </p>

                    <p>
                        Click <span class="font-weight-black primary--text" >SIGN</span> to finger-sign the document. Your signature will be merged into
                        the document when you click <span class="font-weight-black primary--text">NEXT</span>.  To view or print the text of the document
                        before signing, click <span class="font-weight-black primary--text">VIEW DOCUMENT</span>.
                    </p>

                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                v-bind="attrs"
                                v-on="on"
                                elevation="4"
                                block
                                outlined
                                color="primary"
                                class="my-6"
                                @click.stop="view()"
                            >
                                {{isSigned ? "VIEW SIGNED DOCUMENT" : "VIEW DOCUMENT"}}
                            </v-btn>
                        </template>
                        <span>
                            {{isSigned ? "VIEW SIGNED DOCUMENT" : "VIEW EXAMPLE DOCUMENT"}}
                        </span>
                    </v-tooltip>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                v-bind="attrs"
                                v-on="on"
                                elevation="4"
                                block
                                outlined
                                color="primary"
                                class="mb-6"
                                @click.stop="showSig = true"
                            >
                                {{isSigned ? "RE-SIGN" : "SIGN"}}
                            </v-btn>
                        </template>
                        <span>
                            {{isSigned ? "RE-SIGN DOCUMENT WITH CURRENT TERMS" : "SIGN THIS DOCUMENT"}}
                        </span>
                    </v-tooltip>
                </div>
            </v-card-text>
        </v-card>

        <v-divider class="my-3" />

        <v-btn
            text
            @click="back"
        >
            Back
        </v-btn>

        <v-btn
            text
            color="primary"
            @click="next"
            :disabled="!isSigned"
        >
            Next
        </v-btn>

        <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
                <v-icon
                    v-bind="attrs"
                    v-on="on"
                    x-small
                    color="red"
                    v-show="dirty"
                    class="ml-16"
                >mdi-pencil</v-icon>
            </template>
            Values Changed
        </v-tooltip>
        <signature :show="showSig" @cancel="cancel" @save="saveSignature" :signature="glyphs"></signature>
    </div>
</template>

<script>
import complianceMixins from "../common/props"
import ServerFile from "@/plugins/ServerFile"
import DocDef from "./sources/wcwaiver.js"

export default {
	mixins: [complianceMixins],

    data() {
        return {
            step: "2995a539c6904019b2cf003595bed384", // magic, never change!
            vals: null,
            filename: "",
            compdate: 0,
            isSigned: false,
            dirty: false,
            signature: null,
            glyphs: null,
            objectId: null,
            fileHandler: null,
            showSig: false,
            company: {},
            docdef: null
        }
    },

    methods: {
        getDocDef() {
            if (!this.docdef) {
                this.docdef = new DocDef()
            }
            return this.docdef
        },

        getDocValues(instructions) {
            const date = this.store.vue.$time.local()

            const args = {
                instructions: instructions,
                dayOrdinal: this.store.vue.$dayOrdinal(date.day),
                monthName: date.toFormat("LLLL"),
                year: date.year.toString(),
                userName: this.store.userName ? this.store.userName : "MISSING",
                companyName: this.store.companyName ? this.store.companyName : "MISSING",
                date: date.toFormat("LLLL d, yyyy"),
                signature: this.isSigned ? this.signature : this.unsigned
            }

            return args
        },

        async view() {
            //
            //  The user signed and saved before, so the file exists on the server.
            //
            if (this.isSigned && !this.dirty) {
                try {
                    this.fileHandler.getUrlAsBlob()
                    .then(blob => {
                        window.open(blob, "_blank")
                    })
                }

                catch (error) {
                    this.$emit("error", error)
                }
            }
            else {
                //
                //  We generate it here, it will be saved when the user hits next. There are
                //  two versions.  Has not yet signed, and has signed, but not saved.
                //
                try {
                    const args = this.getDocValues(this.isSigned ? "INSTRUCTIONS: Please review the entire agreement. When you are ready, return to the Elite Driver app and click NEXT to save." : "INSTRUCTIONS: Please review the entire agreement. When you are ready, return to the Elite Driver app and finger-sign to complete this agreement.")
                    const dd = this.getDocDef().getDocStructure(args)
                    const blob = await this.generatePdf(dd)
                    window.open(URL.createObjectURL(blob), "_blank")
                }

                catch (error) {
                    this.$emit("error", error)
                }
            }
        },

        cancel() {
            this.showSig = false
        },

        saveSignature(data, glyphs) {
            this.signature = data.data
            this.glyphs = glyphs
            this.isSigned = true
            this.dirty = true
            this.showSig = false
            this.$emit("changed")
        },

        back() {
            if (this.dirty) {
                this.$confirm(this.lostMessage, {icon: "mdi-help", title: "Confirm", color: "primary"})
                .then((res) => {
                    if (res) {
                        this.$emit("back")
                    }
                })
            }
            else {
                this.$emit("back")
            }
        },

        async next() {
            const vm = this

            if (this.dirty) {
                this.$emit("loading", true)

                const args = this.getDocValues("")
                const dd = this.getDocDef().getDocStructure(args)
                const blob = await this.generatePdf(dd)
                const fn = this.store.vue.$uuid.v1().replace(/-/g, "") + ".pdf"
                const fh = new ServerFile({vue: this.store.vue})
                await fh.saveFileObject(blob, this.getCompliancePath(), fn)

                const rec = {
                    step: this.step,
                    user: this.vals.userId,
                    compdate: this.store.vue.$time.local().toMillis(),
                    values: {
                        file: fn,
                        glyphs: this.glyphs,
                    }
                }

                if (this.objectId) {
                    rec.objectId = this.objectId
                }

                this.saveStepData(this.store.vue.$db, rec)
                .then(async () => {
                    if (vm.fileHandler) {
                        await vm.fileHandler.deleteUrl()
                    }
                    vm.dirty = false
                    vm.$emit("next")
                })
                .catch(error => {
                    vm.$emit("error", error)
                })
                .finally(() => {
                    vm.$emit("loading", false)
                })
            }
            else {
                this.$emit("next")
            }
        }
    },

    beforeDestroy() {
        if (this.fileHandler) {
            this.fileHandler.cleanup()
        }
    },

    created() {
        const vm = this

        this.vals = this.getStoreValues(["user*", "company*"])

        this.$emit("loading", true)
        this.getStepData(this.store.vue.$db, this.step, this.vals.userId)
        .then(data => {
            if (data.length === 1) {
                vm.filename = data[0].values.file
                vm.glyphs = data[0].values.glyphs
                vm.compdate = data[0].compdate
                vm.objectId = data[0].objectId
                vm.isSigned = true

                const args = {
                    vue: vm,
                    token: this.vals.userToken,
                    url: vm.getComplianceUrl(vm.filename)
                }
                this.fileHandler = new ServerFile(args)
            }
        })
        .catch(error => {
            vm.$emit("error", error)
        })
        .finally(() => {
            vm.$emit("loading", false)
        })

    },

    components: {
        Signature: () => import("@/components/Signature")
    }
}
</script>